import { useCallback, useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { ENV } from '../config/env';

export type UpdatePrompt = {
  needRefresh: boolean;
  updateReady: () => void;
};

// Run the deploy check as soon as the effect mounts. It previously waited 2s (later
// 1.5s), which meant the banner surfaced seconds after landing - often while the
// user was already mid-task, e.g. opening the chat. The response is ~2KB and is
// already fetched with no-cache, so there is nothing to gain by delaying it.
const DEPLOY_CHECK_LATE_MS = 15000;

// How long the page must be idle before a waiting worker is activated.
const IDLE_BEFORE_AUTO_APPLY_MS = 20000;

// Hard ceiling. Without it, a user who keeps typing never goes idle and the update
// is deferred forever. At this point the deploy is old enough that applying it is
// the lesser risk, provided the page is not actively mid-task.
const MAX_DEFERRAL_MS = 90000;

export function usePwaUpdate(): UpdatePrompt {
  const [bannerVisible, setBannerVisibleState] = useState(false);
  const [reloadPending, setReloadPending] = useState(false);
  const updateSW = useRef<(() => Promise<void>) | null>(null);
  const appliedRef = useRef(false);
  const applyUpdateRef = useRef<(() => void) | null>(null);
  const lastActivityRef = useRef(Date.now());
  const needRefreshRef = useRef(false);
  const pendingSinceRef = useRef(0);
  const bannerVisibleRef = useRef(false);
  const showBanner = useCallback(() => {
    bannerVisibleRef.current = true;
    setBannerVisibleState(true);
  }, []);
  const hadControllerRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    hadControllerRef.current = Boolean(navigator.serviceWorker.controller);

    const applyUpdate = () => {
      if (appliedRef.current) return;
      appliedRef.current = true;
      window.location.reload();
    };
    applyUpdateRef.current = applyUpdate;

    const updateServiceWorker = registerSW({
      immediate: true,
      onNeedRefresh: () => {
        needRefreshRef.current = true;
        if (!pendingSinceRef.current) pendingSinceRef.current = Date.now();
        armBannerFallback();
      },
      onNeedReload: () => {
        setReloadPending(true);
        needRefreshRef.current = false;
        applyUpdate();
      },
      onRegisterError: () => {
        // Service workers are optional; the deploy check below still keeps the
        // UI fresh for sessions that could never register one.
      },
    });
    updateSW.current = updateServiceWorker;

    // The browser only checks for SW scripts on its own schedule. Force an
    // explicit check on every load so a returning user picks up the newest
    // deploy instead of whatever was precached. With skipWaiting + clientsClaim
    // the replacement takes control immediately.
    navigator.serviceWorker.ready
      .then((registration) => {
        void registration.update().catch(() => {});
      })
      .catch(() => {});

    // Once a newer SW takes control the page must reload under it to use the
    // current precache. Only reload when we were previously controlled, so a
    // first-ever claim (nothing stale existed) does not force a needless reload.
    const onControllerChange = () => {
      if (!hadControllerRef.current) return;
      applyUpdate();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // Compare the deployed build id against the one this page was built with.
    //
    // This used to bail out whenever a service worker controlled the page, which
    // left exactly the returning-visitor case relying on the browser happening to
    // re-fetch sw.js on its own schedule. The banner then appeared seconds or
    // minutes after load, at an arbitrary moment.
    //
    // The build id is authoritative regardless of service worker state, so it is
    // checked for everyone. It also settles the SW-handles-the-reload question:
    // when a controller exists we ask the waiting worker to activate rather than
    // reloading immediately, so the user is not dumped out of whatever they were
    // doing (solving Turnstile, mid-conversation).
    const runDeployCheck = () => {
      if (appliedRef.current) return;
      // Use "/" not "/index.html": Pages 308-redirects /index.html to /, and the
      // redirect target is not guaranteed to be the freshly deployed shell.
      // The query string is what actually busts the CDN cache.
      void fetch(`/?deployCheck=${Date.now()}`, { headers: { 'Cache-Control': 'no-cache' } })
        .then((res) => (res.ok ? res.text() : null))
        .then((html) => {
          if (!html || appliedRef.current) return;
          // Tolerate both `<meta ...>` and the self-closing `<meta ... />` that
          // Vite emits; the strict form silently matched nothing.
          const match = html.match(/<meta\s+name="build-id"\s+content="([^"]+)"\s*\/?>/);
          if (!match || !match[1] || match[1] === ENV.BUILD_ID) return;

          if (navigator.serviceWorker.controller) {
            // A worker is in control, so the waiting one can be promoted. The idle
            // timer applies it; the banner is only the fallback for a tab that
            // never goes idle (or where the user would rather choose).
            needRefreshRef.current = true;
            if (!pendingSinceRef.current) pendingSinceRef.current = Date.now();
            armBannerFallback();
            return;
          }
          // No service worker to hand off to: this is the only way to pick up the
          // new deploy, so reload.
          applyUpdate();
        })
        .catch(() => {});
    };

    // First check straight away, so a pending deploy is known before the user can
    // start interacting with the page. setTimeout(0) would still cost a frame.
    runDeployCheck();
    // Second check catches a deploy that lands while the tab is open.
    const lateTimer = window.setTimeout(runDeployCheck, DEPLOY_CHECK_LATE_MS);

    // Idle auto-apply.
    //
    // Auto-reloading on a timer would destroy whatever the user was doing - a
    // half-typed chat message, an edited cart. So the swap only happens once the
    // page has been untouched for IDLE_BEFORE_AUTO_APPLY_MS, and only while no
    // text field or the chat itself has focus. A waiting worker is promoted and
    // controllerchange performs the reload.
    //
    // This keeps the property that skipWaiting:false exists for: the new worker
    // never takes over while the page is in active use.
    // Only deliberate interaction counts as activity. Scrolling, pointer movement and
    // focus changes used to reset the idle timer, so a user reading the page never
    // reached the idle threshold at all and the update was deferred indefinitely -
    // eventually surfacing the banner long after they stopped caring.
    //
    // Reading the page is not a reason to defer an update; typing or clicking into
    // something is.
    const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'touchstart', 'input'] as const;
    const markActive = () => { lastActivityRef.current = Date.now(); };
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, markActive, { passive: true });
    }

    const pageIsBusy = () => {
      const active = document.activeElement;
      // Never swap while someone is typing or a dropdown/menu is open.
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT' || (active as HTMLElement).isContentEditable)) return true;
      // The chat widget is a fixed overlay; if it is open, an update would
      // interrupt the conversation or reset the Turnstile solve.
      if (document.querySelector('[data-testid="chat-panel"]')) return true;
      return false;
    };

    let bannerTimer: number | undefined;
    const idleTimer = window.setInterval(() => {
      if (appliedRef.current || !needRefreshRef.current) return;
      // Banner is already up: the decision is the user's now.
      if (bannerVisibleRef.current) return;

      const idleFor = Date.now() - lastActivityRef.current;
      const waitingFor = Date.now() - pendingSinceRef.current;
      if (idleFor < IDLE_BEFORE_AUTO_APPLY_MS && waitingFor < MAX_DEFERRAL_MS) return;

      if (pageIsBusy()) {
        // Mid-task: a text field has focus or the chat is open. Never swap under
        // either. Surface the banner so the user decides, rather than deferring
        // silently until they have stopped caring.
        showBanner();
        return;
      }

      // Idle and safe: promote the waiting worker. controllerchange reloads.
      // The banner is never shown in this path, so a deploy is applied without the
      // user seeing anything at all.
      void Promise.resolve(updateSW.current?.()).catch(() => {});
      clearInterval(idleTimer);
      if (bannerTimer) window.clearTimeout(bannerTimer);
    }, 5000);

    // If the update still cannot be auto-applied well after it was discovered,
    // offer it. Armed only once an update is actually pending, so a clean page
    // never arms it and a late-discovered update is not rushed.
    const armBannerFallback = () => {
      if (bannerTimer) window.clearTimeout(bannerTimer);
      bannerTimer = window.setTimeout(() => {
        if (appliedRef.current || !needRefreshRef.current || bannerVisibleRef.current) return;
        showBanner();
      }, Math.max(0, MAX_DEFERRAL_MS - (Date.now() - pendingSinceRef.current)));
    };

    return () => {
      window.clearTimeout(lateTimer);
      window.clearInterval(idleTimer);
      if (bannerTimer) window.clearTimeout(bannerTimer);
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, markActive);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      void updateServiceWorker(false);
    };
  }, []);

  const updateReady = useCallback(() => {
    if (reloadPending) return;
    needRefreshRef.current = false;
    bannerVisibleRef.current = false;
    setBannerVisibleState(false);
    // updateSW() activates the waiting worker; controllerchange then reloads.
    // If nothing was waiting, reload immediately so the promise is always kept.
    void Promise.resolve(updateSW.current?.()).catch(() => applyUpdateRef.current?.());
  }, [reloadPending]);

  // needRefresh drives the banner; the auto-apply path sets it without ever
  // rendering UI, so a deploy normally lands silently.
  return { needRefresh: bannerVisible, updateReady };
}